import React, { useState } from 'react';
import { 
  Box, Container, Typography, TextField, Button, Paper,
  Card, CardContent, MenuItem, Select, FormControl, InputLabel,
  Divider, ToggleButtonGroup, ToggleButton
} from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CheckIcon from '@mui/icons-material/Check';
import LockIcon from '@mui/icons-material/Lock';

const Payment: React.FC = () => {
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [formData, setFormData] = useState({
    cardNumber: '',
    cardName: '',
    expiryDate: '',
    cvv: '',
    plan: 'pro'
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Payment:', formData);
  };

  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: 'calc(100vh - 64px - 300px)', py: { xs: 4, sm: 6, md: 8 } }}>
      <Container maxWidth="lg">
        <Typography variant="h3" gutterBottom fontWeight={700} sx={{ fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' } }}>
          Complete Your Purchase
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Choose your plan and payment method
        </Typography>

        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
          gap: 4
        }}>
          <Box>
            <Paper sx={{ p: { xs: 2, sm: 3, md: 4 }, mb: 3 }}>
              <Typography variant="h6" gutterBottom fontWeight={600}>
                Select Plan
              </Typography>
              <FormControl fullWidth>
                <InputLabel>Plan</InputLabel>
                <Select
                  name="plan"
                  value={formData.plan}
                  label="Plan"
                  onChange={(e) => setFormData({ ...formData, plan: e.target.value })}
                >
                  <MenuItem value="free">Free - $0/month</MenuItem>
                  <MenuItem value="pro">Pro - $29/month</MenuItem>
                  <MenuItem value="enterprise">Enterprise - Custom</MenuItem>
                </Select>
              </FormControl>
            </Paper>

            <Paper sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
              <Typography variant="h6" gutterBottom fontWeight={600}>
                Payment Method
              </Typography>
              <ToggleButtonGroup
                value={paymentMethod}
                exclusive
                onChange={(e, newValue) => newValue && setPaymentMethod(newValue)}
                fullWidth
                sx={{ mb: 3 }}
              >
                <ToggleButton value="card" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  <CreditCardIcon sx={{ mr: { xs: 0.5, sm: 1 }, fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Credit </Box>Card
                </ToggleButton>
                <ToggleButton value="paypal" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  PayPal
                </ToggleButton>
              </ToggleButtonGroup>

              {paymentMethod === 'card' && (
                <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <TextField
                    fullWidth
                    label="Card Number"
                    name="cardNumber"
                    value={formData.cardNumber}
                    onChange={handleChange}
                    placeholder="1234 5678 9012 3456"
                    required
                  />

                  <TextField
                    fullWidth
                    label="Cardholder Name"
                    name="cardName"
                    value={formData.cardName}
                    onChange={handleChange}
                    placeholder="John Doe"
                    required
                  />

                  <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 2 }}>
                    <TextField
                      fullWidth
                      label="Expiry Date"
                      name="expiryDate"
                      value={formData.expiryDate}
                      onChange={handleChange}
                      placeholder="MM/YY"
                      required
                    />
                    <TextField
                      fullWidth
                      label="CVV"
                      name="cvv"
                      value={formData.cvv}
                      onChange={handleChange}
                      placeholder="123"
                      required
                    />
                  </Box>

                  <Button 
                    type="submit" 
                    variant="contained" 
                    size="large" 
                    fullWidth
                    sx={{ mt: 2 }}
                  >
                    Complete Payment
                  </Button>
                </Box>
              )}

              {paymentMethod === 'paypal' && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Button 
                    variant="contained" 
                    size="large"
                    fullWidth
                  >
                    Continue with PayPal
                  </Button>
                </Box>
              )}
            </Paper>
          </Box>

          <Box>
            <Card sx={{ position: { xs: 'relative', md: 'sticky' }, top: 100 }}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  Order Summary
                </Typography>
                
                <Box sx={{ my: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography>Pro Plan</Typography>
                    <Typography fontWeight={600}>$29.00</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography color="text.secondary">Tax</Typography>
                    <Typography>$0.00</Typography>
                  </Box>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h6" fontWeight={600}>Total</Typography>
                    <Typography variant="h6" fontWeight={700}>$29.00</Typography>
                  </Box>
                </Box>

                <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 2, mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                    What's Included:
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {[
                      '10,000 screenshots/month',
                      'Advanced customization',
                      'Priority support',
                      'High resolution (4K)',
                      'Webhook integration',
                      'API access'
                    ].map((item, index) => (
                      <Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
                        <CheckIcon sx={{ fontSize: 16, mr: 1 }} />
                        <Typography variant="body2">{item}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, color: 'text.secondary' }}>
                  <LockIcon fontSize="small" />
                  <Typography variant="body2">
                    Secure payment powered by Stripe
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Payment;
